// src/components/SvgPreview.tsx
//
// Renders a raw SVG markup string (from a ```svg fence) inline, as an
// actual visual — not as syntax-highlighted text.
//
// SECURITY: model-generated SVG is untrusted input. SVG can carry <script>
// tags, event-handler attributes (onload, onclick, ...), and other vectors
// that would execute with full access to this app's origin (including
// localStorage, where the JWT lives) if injected raw. DOMPurify's SVG
// profile strips all of that while preserving the actual visual markup —
// this is NOT the same as just trusting the model's output.
import React, { useMemo } from 'react';
import DOMPurify from 'dompurify';

interface Props {
  code: string;
}

const SVG_PURIFY_CONFIG = {
  USE_PROFILES: { svg: true, svgFilters: true },
  // Belt-and-suspenders on top of the SVG profile: explicitly forbid the
  // tags/attributes most commonly used for SVG-based XSS.
  FORBID_TAGS: ['script', 'foreignObject', 'iframe'],
  FORBID_ATTR: ['onload', 'onclick', 'onerror', 'onmouseover', 'href'],
};

export default function SvgPreview({ code }: Props) {
  const clean = useMemo(() => {
    try {
      return String(DOMPurify.sanitize(code, SVG_PURIFY_CONFIG as any));
    } catch {
      return '';
    }
  }, [code]);

  if (!clean.trim()) {
    return (
      <div className="diagram-error">
        <p>⚠ Couldn't render this SVG (invalid, empty, or unsafe markup was removed).</p>
        <pre>{code}</pre>
      </div>
    );
  }

  return <div className="svg-preview" dangerouslySetInnerHTML={{ __html: clean }} />;
}